import isFinite from 'lodash/isFinite';
import clamp from 'lodash/clamp';
import find from 'lodash/find';
import sumBy from 'lodash/sumBy';
import last from 'lodash/last';
import reduce from 'lodash/reduce';
import keys from 'lodash/keys';
import map from 'lodash/map';
import toNumber from 'lodash/toNumber';
import isNil from 'lodash/isNil';
import { includes } from 'lodash';

import type { PartnerName } from '@jerry-serverless/analyticslib/partner';
import { validPartnerNameParam } from '@jerry-serverless/tools/lib/validators';

import { WELCOME_ENABLED_LIST } from '../partner/partner_interstitial_list';
import { parseSignupParams } from '../../types/signup_params';

import type { ABTestVariation, FeatureData, FeatureInput } from './types';
import abTests from './ab_tests';

// Cookie name for storing the Jerry signup app feature states.
export const FEATURES_COOKIE_NAME = 'jsfeat';

const ASK_REFERRAL_SOURCES = new Set(['google', 'googlebrand']);

/**
 * Extracts the features state from the cookie object.
 *
 * @param cookieObj
 */
export const getFeaturesFromCookie = (cookieObj: Record<string, string>): Record<string, string> => {
  return reduce(
    (cookieObj[FEATURES_COOKIE_NAME] || '').split(','),
    (obj, kv) => {
      const [k, v] = kv.split(':', 2);
      return {
        ...obj,
        [k]: v,
      };
    },
    {} as Record<string, string>
  );
};

/**
 * Converts the features sate into a string that can be used as value for `Set-Cookie` header.
 * NOTE: This is the invert of `getFeaturesFromCookie`
 *
 * @param featuresCookieObj
 */
export const getSetCookieStringFromFeatures = (featuresCookieObj: Record<string, string>): string => {
  const cookie = map(keys(featuresCookieObj), (key) => {
    return `${key}:${featuresCookieObj[key]}`;
  }).join(',');
  return `${FEATURES_COOKIE_NAME}=${cookie}; Path=/`;
};

/**
 * Parses float value from env variable string and clamps it between 0 and 1.
 *
 * @param strVal
 */
export const getPercentEnable = (strVal?: string): number => {
  const val = parseFloat(strVal || '');
  return clamp(isFinite(val) ? val : 0, 0, 1);
};

/**
 * Selects a variation from the list based on their enabled percents.
 *
 * @param variations
 */
export const pickABTestVariation = (variations: ABTestVariation[]): ABTestVariation | undefined => {
  const val = Math.random();
  return (
    find(variations, (variation, idx) => {
      const threshold = sumBy(variations.slice(0, idx + 1), ({ percentEnable }) => percentEnable || 0);
      return val < threshold;
    }) || last(variations)
  );
};

/**
 * Get the A/B test value from url or cookie
 * @param value - value in Url or Cookie, it can be "true"|"false" or string number like "1"|"2"..
 * @returns boolean or number or undefined
 */
export const parseValueFromUrlOrCookie = (value: undefined | string | number) => {
  if (isNil(value) || value === '') return undefined;
  if (value === 'true' || value === 'false') {
    return value === 'true' ? true : false;
  } else {
    const num = toNumber(value);
    return isFinite(num) ? num : undefined;
  }
};

/**
 * Determines the status (enabled/disabled) for FeatureData
 * @returns FeatureData
 */
export const getFeatureValues = ({
  appData,
  queryObj,
  user,
  partnerSignup,
  growthbook,
  prefillQuotingVariation,
}: Omit<FeatureInput, 'featureData' | 'cookieObj'>): FeatureData => {
  /**
   * Check if feature was previously enabled (i.e. stored in User's DB record).
   * Don't check if 'partnerSignup' is true since the data needed to check 'previouslyEnabled'
   * won't be available when user enters funnel. We also shouldn't check if users are coming
   * from the mobile app for the first time for the same reason
   *
   * It is applied for askICVBeforeOnboarding, askReferralSource, askShopFor, and askVehicleOwnership features
   */
  const partnerData = appData.partner;
  const mobileAppView = !!appData.mobileAppView;
  const shouldCheckPreviouslyEnabledData = !partnerSignup && user && (!mobileAppView || user?.isSigned);
  const params = parseSignupParams(user?.signupParams);
  const namePrefilledFromUrlParam =
    !!partnerData &&
    includes<PartnerName>(WELCOME_ENABLED_LIST, partnerData.partner) &&
    validPartnerNameParam(partnerData.data.fname || '', partnerData.data.lname || '');

  /**
   * Show name and phone number prefilled landing page for signup.
   * Mostly used from partner side:
   * 1) Partner API traffic.
   * 2) When partner sends us the valid name through url params; eg; signup/utm_source=ppartner?fname=stan&lname=moon&ph=1112223333'.
   *
   * For the partner 'Welcoming' page with prefilled name, ph. We need to check the validity of name params that partner sent to us.
   *
   * Check case if traffic is from API.
   */
  const askPhoneNamePrefilled = namePrefilledFromUrlParam || !!(partnerSignup && user?.name);

  /**
   * Disable for partner API users or partner traffic with prefilled name/phone page.
   */
  let askICVBeforeOnboarding: boolean;
  if (askPhoneNamePrefilled) {
    askICVBeforeOnboarding = true;
    // If Var1,2,3, don't ask ICV before phone step
    if (prefillQuotingVariation) {
      askICVBeforeOnboarding = false;
    }
  } else {
    askICVBeforeOnboarding = shouldCheckPreviouslyEnabledData ? params.reorderSignUpEnabled : true;
  }
  // For facebook landing interstitial, skip icv questions: https://app.asana.com/0/1202292931304613/1209095067923609/f
  if (partnerData?.partner === 'facebook' && appData.utmSourceFromSignupQuery === 'facebook') {
    askICVBeforeOnboarding = false;
  }

  /**
   * Disable for any partner traffic, except certain sources.
   */
  const canAskReferralSource =
    !(queryObj.utm_source || partnerData) ||
    ASK_REFERRAL_SOURCES.has(queryObj.utm_source) ||
    (partnerData && ASK_REFERRAL_SOURCES.has(partnerData.partner));

  let askReferralSource: boolean;
  if (!canAskReferralSource) {
    askReferralSource = false;
  } else {
    askReferralSource = shouldCheckPreviouslyEnabledData ? !!params.askReferral : true;
  }

  /**
   * Disable for partner API users since we use another step shopForPartner for users from partner to choose shop for
   * Don't block partner traffic with prefilled name/phone page any more: https://app.asana.com/0/1200876481063191/1201789967699858/f
   * Disable if user is from facebook or cbautogroup AND has 'shopfor' url query parm
   */
  const canAskShopFor =
    !partnerSignup &&
    !((partnerData?.partner === 'facebook' || partnerData?.partner === 'cbautogroup') && queryObj['shopfor']);

  let askShopFor: boolean;
  if (!canAskShopFor) {
    askShopFor = false;
  } else {
    askShopFor = shouldCheckPreviouslyEnabledData ? params.shopForEnabled : true;
  }

  const askVehicleOwnership = shouldCheckPreviouslyEnabledData
    ? params.askVehicleOwnership
    : !!growthbook?.feature(abTests.AskVehicleOwnership.storageKey).on;

  const stashInterstital =
    partnerData?.partner === 'stash' ? !!growthbook?.feature(abTests.StashInterstital.storageKey).on : false;

  const featureData: FeatureData = {
    askShopFor,
    askICVBeforeOnboarding,
    askReferralSource,
    askPhoneNamePrefilled,
    namePrefilledFromUrlParam,
    askVehicleOwnership,
    stashInterstital,
  };

  return featureData;
};
